import { useEffect, useState } from "react";
import { Mail, ShieldOff, Play, Loader2, Send, Download } from "lucide-react";
import { toast } from "sonner";
import AppLayout from "../components/AppLayout";
import NeedsReviewPanel from "../components/outreach/NeedsReviewPanel";
import OutreachTable from "../components/outreach/OutreachTable";
import DncManager from "../components/outreach/DncManager";
import OutreachSettingsCard from "../components/outreach/OutreachSettingsCard";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const Stat = ({ label, value, testid }) => (
  <div className="rounded-xl border border-slate-800 bg-[#131C31] px-4 py-3">
    <p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p>
    <p data-testid={testid} className="font-display font-black text-xl text-white mt-0.5">{value}</p>
  </div>
);

export default function OutreachPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("queue");
  const [sum, setSum] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [running, setRunning] = useState(false);

  useEffect(() => { api.get("/outreach/summary").then(({ data }) => setSum(data)).catch(() => {}); }, [reloadKey]);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/outreach/run-now");
      toast.info(`Send queue: ${data.send.result} · Inbox: ${data.replies.result}`);
      setReloadKey((k) => k + 1);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setRunning(false);
    }
  };

  const exportCasl = async () => {
    try {
      const res = await api.get("/outreach/casl-log.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `casl-log-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Could not export the CASL log");
    }
  };

  const sendDigest = async () => {
    try {
      const { data } = await api.post("/outreach/digest/send-now");
      if (data.result === "sent") toast.success(`Digest ${data.mocked ? "simulated (MOCKED)" : "sent"}: ${data.new} new, ${data.total} waiting`);
      else toast.info(data.result);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const tabCls = (t) => `px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2 transition-colors ${tab === t ? "bg-amber-500/10 text-amber-400 border border-amber-500/20" : "text-slate-400 hover:text-white"}`;

  return (
    <AppLayout>
      <div data-testid="outreach-page" className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto animate-fade-in">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <h1 className="font-display font-black text-3xl tracking-tight">Outreach</h1>
            <p className="text-sm text-slate-400 mt-1">Review, approve and track email sequences from alex@kingofit.ca</p>
          </div>
          {isAdmin && (
            <div className="flex flex-wrap gap-2">
              <button data-testid="export-casl-log-button" onClick={exportCasl} className="rounded-lg px-4 py-2 text-sm border border-slate-700 text-slate-300 hover:border-amber-500/50 inline-flex items-center gap-2 transition-colors">
                <Download className="w-4 h-4" /> CASL log CSV
              </button>
              <button data-testid="send-digest-now-button" onClick={sendDigest} className="rounded-lg px-4 py-2 text-sm border border-slate-700 text-slate-300 hover:border-amber-500/50 inline-flex items-center gap-2 transition-colors">
                <Send className="w-4 h-4" /> Send digest now
              </button>
              <button data-testid="run-queue-now-button" onClick={runNow} disabled={running} className="rounded-lg px-4 py-2 text-sm border border-slate-700 text-slate-300 hover:border-amber-500/50 inline-flex items-center gap-2 transition-colors">
                {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Run queue now
              </button>
            </div>
          )}
        </div>

        {sum?.mocked && (
          <div data-testid="outreach-mocked-banner" className="rounded-lg border border-sky-500/30 bg-sky-500/10 text-sky-200 text-sm px-4 py-2.5 mb-5">
            MOCKED mode: Microsoft 365 keys aren't configured yet, so "sent" emails are simulated and the inbox isn't checked.
          </div>
        )}

        {sum?.paused && (
          <div data-testid="outreach-paused-alert" className="rounded-lg border border-red-500/40 bg-red-500/10 text-red-200 text-sm px-4 py-3 mb-5">
            <b>Sending is paused.</b> {sum.pause_reason}. Turn off "Pause all sending" below once resolved.
          </div>
        )}
        {sum?.bounces_today > 0 && (
          <div data-testid="outreach-bounce-flag" className="rounded-lg border border-orange-500/40 bg-orange-500/10 text-orange-200 text-sm px-4 py-2.5 mb-5">
            {sum.bounces_today} bounce(s) today. Sending pauses automatically at 3.
          </div>
        )}

        {sum && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
            <Stat label="Drafts" value={sum.counts.Draft} testid="stat-drafts" />
            <Stat label="Scheduled" value={sum.counts.Scheduled} testid="stat-scheduled" />
            <Stat label="Sent" value={sum.counts.Sent + sum.counts.Replied} testid="stat-sent" />
            <Stat label="First emails today" value={`${sum.step1_today}/${sum.daily_cap}`} testid="stat-step1-today" />
            <Stat label="Send window" value={sum.window_open ? "Open" : "Closed"} testid="stat-window" />
          </div>
        )}

        <NeedsReviewPanel reloadKey={reloadKey} />

        <OutreachSettingsCard isAdmin={isAdmin} onChanged={() => setReloadKey((k) => k + 1)} />

        <div className="flex gap-2 mb-4">
          <button data-testid="tab-queue" onClick={() => setTab("queue")} className={tabCls("queue")}><Mail className="w-4 h-4" /> Emails</button>
          <button data-testid="tab-dnc" onClick={() => setTab("dnc")} className={tabCls("dnc")}><ShieldOff className="w-4 h-4" /> Do-Not-Contact</button>
        </div>

        {tab === "queue"
          ? <OutreachTable key={reloadKey} isAdmin={isAdmin} onChanged={() => setReloadKey((k) => k + 1)} />
          : <DncManager isAdmin={isAdmin} />}
      </div>
    </AppLayout>
  );
}
